/**
 * WorkspaceSidebar - Left sliding panel for workspace selection
 *
 * Features:
 * - Collapsed state shows just an icon
 * - Expanded state shows full workspace list with search
 * - Smooth slide animation
 * - Click outside to close
 */

import React, { useState, useRef, useEffect } from 'react';
import { Building2, ChevronRight, ChevronLeft, Search, Check, FolderOpen, Plus, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { useI18n } from '../i18n';

const WorkspaceSidebar = () => {
  const { t } = useI18n();
  const {
    activeWorkspace,
    workspaces,
    loading,
    switchWorkspace,
    fetchWorkspaces,
  } = useWorkspace();

  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [filteredWorkspaces, setFilteredWorkspaces] = useState([]);
  const [switching, setSwitching] = useState(false);
  const sidebarRef = useRef(null);
  const searchInputRef = useRef(null);

  // Filter workspaces based on search
  useEffect(() => {
    if (!search.trim()) {
      setFilteredWorkspaces(workspaces);
    } else {
      const term = search.toLowerCase();
      setFilteredWorkspaces(
        workspaces.filter(
          (ws) =>
            ws.name?.toLowerCase().includes(term) ||
            ws.company_name?.toLowerCase().includes(term) ||
            ws.city?.toLowerCase().includes(term)
        )
      );
    }
  }, [search, workspaces]);

  // Close sidebar when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearch('');
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Focus search input when sidebar opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 300);
    }
  }, [isOpen]);

  // Fetch workspaces when sidebar opens
  useEffect(() => {
    if (isOpen) {
      fetchWorkspaces();
    }
  }, [isOpen, fetchWorkspaces]);

  // Handle workspace selection
  const handleSelect = async (workspaceId) => {
    if (workspaceId === activeWorkspace?.id) {
      setIsOpen(false);
      return;
    }

    setSwitching(true);
    const success = await switchWorkspace(workspaceId);
    setSwitching(false);

    if (success) {
      setIsOpen(false);
      setSearch('');
      window.location.reload();
    }
  };

  // Get workspace initials for collapsed view
  const getInitials = (name) => {
    if (!name) return 'W';
    return name.split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase();
  };

  // Get workspace logo URL (supports both logo_url and logo_base64)
  const getLogoUrl = (workspace) => {
    if (!workspace) return null;
    if (workspace.logo_url) return workspace.logo_url;
    if (workspace.logo_base64) {
      // Check if it already has data URI prefix
      if (workspace.logo_base64.startsWith('data:')) return workspace.logo_base64;
      return `data:image/png;base64,${workspace.logo_base64}`;
    }
    return null;
  };

  // Workspace Avatar component - shows logo or initials
  const WorkspaceAvatar = ({ workspace, size = 'md', isActive = false, className = '' }) => {
    const logoUrl = getLogoUrl(workspace);
    const sizeClasses = size === 'lg' ? 'w-12 h-12' : 'w-10 h-10';

    if (logoUrl) {
      return (
        <div className={`${sizeClasses} rounded-lg overflow-hidden flex-shrink-0
                        border ${isActive ? 'border-brand-green/50' : 'border-brand-green/30'} ${className}`}>
          <img
            src={logoUrl}
            alt={workspace?.name || 'Workspace'}
            className="w-full h-full object-cover"
            onError={(e) => {
              // Fallback to initials on error
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
          <div className={`${sizeClasses} bg-brand-green/20 items-center justify-center
                          text-brand-green font-bold text-sm hidden`}>
            {getInitials(workspace?.name)}
          </div>
        </div>
      );
    }

    return (
      <div className={`${sizeClasses} rounded-lg bg-brand-green/20 flex items-center justify-center
                      text-brand-green font-bold text-sm border
                      ${isActive ? 'border-brand-green/50' : 'border-brand-green/30'}
                      flex-shrink-0 ${className}`}>
        {getInitials(workspace?.name)}
      </div>
    );
  };

  return (
    <>
      {/* Overlay when sidebar is open */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9998] transition-opacity duration-300"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Sidebar - Hidden on mobile, visible from lg breakpoint */}
      <div
        ref={sidebarRef}
        className={`fixed left-0 top-0 h-full z-[9999] transition-all duration-300 ease-in-out
                    hidden lg:block
                    ${isOpen ? 'w-64 laptop:w-72 xl:w-80' : 'w-16'}`}
      >
        {/* Main sidebar container */}
        <div className="h-full bg-brand-dark/95 backdrop-blur-xl border-r border-brand-green/30
                        shadow-2xl shadow-brand-green/10 flex flex-col">

          {/* Header - Toggle button */}
          <div className="p-3 border-b border-brand-green/20 flex items-center justify-between">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className={`flex items-center gap-3 p-2 rounded-lg transition-all duration-200
                         hover:bg-brand-green/20 ${isOpen ? 'w-full' : 'w-full justify-center'}`}
            >
              {isOpen ? (
                <>
                  <WorkspaceAvatar workspace={activeWorkspace} isActive={true} />
                  <div className="flex-1 text-left min-w-0">
                    <p className="text-white font-medium truncate text-sm" title={activeWorkspace?.name || ''}>
                      {activeWorkspace?.name || t('workspace.select', 'Sélectionner un workspace')}
                    </p>
                    {activeWorkspace?.company_name && (
                      <p className="text-xs text-gray-400 truncate" title={activeWorkspace.company_name}>{activeWorkspace.company_name}</p>
                    )}
                  </div>
                  <X className="w-5 h-5 text-gray-400 hover:text-white flex-shrink-0" />
                </>
              ) : (
                loading ? (
                  <div className="w-10 h-10 rounded-lg bg-brand-green/20 flex items-center justify-center
                                  border border-brand-green/30">
                    <Building2 className="w-5 h-5 text-brand-green animate-pulse" />
                  </div>
                ) : (
                  <WorkspaceAvatar
                    workspace={activeWorkspace}
                    isActive={true}
                    className="hover:ring-2 hover:ring-brand-green/50 transition-all"
                  />
                )
              )}
            </button>
          </div>

          {/* Expanded content */}
          {isOpen && (
            <>
              {/* Search */}
              <div className="p-3 border-b border-brand-green/20">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder={t('workspace.searchPlaceholder', 'Rechercher un workspace...')}
                    className="w-full pl-10 pr-4 py-2.5 bg-brand-black/50 border border-brand-green/20
                               rounded-lg text-sm text-white placeholder-gray-400
                               focus:outline-none focus:border-brand-green/50 transition-colors"
                  />
                </div>
              </div>

              {/* Workspace List */}
              <div className="flex-1 overflow-y-auto">
                {filteredWorkspaces.length === 0 ? (
                  <div className="px-4 py-12 text-center text-gray-400 text-sm">
                    <FolderOpen className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>{t('workspace.noWorkspaces', 'Aucun workspace disponible')}</p>
                  </div>
                ) : (
                  <div className="p-2 space-y-1">
                    {filteredWorkspaces.map((ws) => (
                      <button
                        key={ws.id}
                        onClick={() => handleSelect(ws.id)}
                        disabled={switching}
                        className={`w-full px-3 py-3 text-left transition-all duration-200 rounded-lg
                                   flex items-center gap-3 group
                                   ${ws.id === activeWorkspace?.id
                                     ? 'bg-brand-green/20 border border-brand-green/40'
                                     : 'hover:bg-brand-green/10 border border-transparent'
                                   }`}
                      >
                        <WorkspaceAvatar
                          workspace={ws}
                          isActive={ws.id === activeWorkspace?.id}
                          className={ws.id !== activeWorkspace?.id ? 'group-hover:ring-2 group-hover:ring-brand-green/30' : ''}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-white font-medium truncate text-sm" title={ws.name}>{ws.name}</span>
                            {ws.id === activeWorkspace?.id && (
                              <Check className="w-4 h-4 text-brand-green flex-shrink-0" />
                            )}
                          </div>
                          {ws.company_name && (
                            <span className="text-xs text-gray-400 truncate block" title={`${ws.company_name}${ws.city ? ` • ${ws.city}` : ''}`}>
                              {ws.company_name}
                              {ws.city && ` • ${ws.city}`}
                            </span>
                          )}
                          <span className="text-xs text-brand-green/70 flex items-center gap-1 mt-0.5">
                            <span className="inline-block w-1.5 h-1.5 rounded-full bg-brand-green/50"></span>
                            {ws.scans_count || 0} {(ws.scans_count || 0) === 1 ? t('workspace.scan', 'scan') : t('workspace.scans', 'scans')}
                          </span>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Footer - Link to workspace management */}
              <div className="p-3 border-t border-brand-green/20 bg-brand-black/30">
                <Link
                  to="/workspaces"
                  onClick={() => setIsOpen(false)}
                  className="flex items-center justify-center gap-2 w-full py-2.5 px-4
                             bg-brand-green/10 hover:bg-brand-green/20 border border-brand-green/30
                             rounded-lg text-sm text-brand-green hover:text-white
                             transition-all duration-200"
                >
                  <Plus className="w-4 h-4" />
                  {t('workspace.manageAll', 'Gérer les workspaces')}
                </Link>
              </div>
            </>
          )}

          {/* Collapsed state - just show expand hint */}
          {!isOpen && (
            <div className="flex-1 flex flex-col items-center py-4">
              <div className="w-8 h-8 rounded-full bg-brand-green/10 flex items-center justify-center
                              text-brand-green/50 hover:text-brand-green hover:bg-brand-green/20
                              transition-colors cursor-pointer"
                   onClick={() => setIsOpen(true)}>
                <ChevronRight className="w-4 h-4" />
              </div>
            </div>
          )}
        </div>
      </div>

    </>
  );
};

export default WorkspaceSidebar;
